import { Tabs as MuiTabs } from "@mui/material";
import { type CSSObject, styled, type Theme } from "@mui/material/styles";

import {
  BOOKMARK_SIZE,
  END_ICON_ATTRIBUTE,
  ICON_ONLY_ATTRIBUTE,
  SUB_NAV_GAP,
  SUB_NAV_PADDING_BOTTOM,
  TAB_GLYPH,
  TAB_ICON_GAP,
  TAB_LABEL,
  TOP_BAR_BORDER,
  TOP_BAR_SIZES,
} from "ui/tabs/tabs.constants";
import { type TabsAppearance, type TabsSize } from "ui/tabs/tabs.types";

import { focusRing } from "theme/theme.helpers";

type TabsRootProps = { appearance: TabsAppearance; size: TabsSize };

/**
 * Figma's `state` × `active` cells for one strip. Exported so the Storybook page paints its hovered,
 * pressed and focused cells from these objects rather than from a copy of them.
 *
 * `focused` is empty where Figma's focus is a `focusRipple` — that is MUI's own pulse, recoloured by
 * the strip, not a style. A pressed selected tab looks like a resting one in every strip.
 */
export type TabStates = {
  rest: CSSObject;
  hovered: CSSObject;
  pressed: CSSObject;
  focused: CSSObject;
  selected: CSSObject;
  selectedHovered: CSSObject;
};

export const tabStates = (theme: Theme, appearance: TabsAppearance): TabStates => {
  const { palette, shape } = theme;

  switch (appearance) {
    case "top-bar":
      return {
        rest: { backgroundColor: palette.background.paper, color: palette.primary.main },
        hovered: { color: palette.primary.dark },
        pressed: { backgroundColor: palette.secondary.lighter },
        focused: {},
        selected: {
          borderColor: palette.secondary.main,
          backgroundColor: palette.secondary.main,
          color: palette.secondary.contrastText,
        },
        selectedHovered: {
          borderColor: palette.secondary.dark,
          backgroundColor: palette.secondary.dark,
          color: palette.secondary.contrastText,
        },
      };
    case "bookmark":
      return {
        rest: { backgroundColor: palette.grey[200], color: palette.primary.main },
        hovered: { color: palette.primary.dark },
        pressed: { backgroundColor: palette.grey[300] },
        focused: {},
        selected: { backgroundColor: palette.background.default, color: palette.secondary.main },
        selectedHovered: { color: palette.secondary.dark },
      };
    case "sub-nav":
      return {
        rest: { color: palette.primary.main },
        hovered: { color: palette.primary.dark },
        pressed: {},
        focused: {
          // A string, not a number: the Storybook cells paint this through `sx`, which would read a
          // bare 4 as 4 × `shape.borderRadius`.
          borderRadius: `${shape.small}px`,
          backgroundColor: palette.common.white,
          boxShadow: focusRing(palette.primary.main),
        },
        selected: { color: palette.secondary.main },
        selectedHovered: { color: palette.secondary.dark },
      };
  }
};

/**
 * Every tab rule is written from the strip, not from `Tab`: MUI's own tab rules carry two classes on
 * their states (`.Mui-selected`, `.Mui-disabled`), and a rule on the tab itself would tie them.
 */
const TAB = "& .MuiTab-root";
const ICON_ONLY = `&[${ICON_ONLY_ATTRIBUTE}]`;
const HOVER = "@media (hover: hover)";

/**
 * The state ladder every strip shares. Order is load-bearing: hover and press sit before `.Mui-selected`,
 * which ties them on specificity and must win, so a selected tab neither greys on press nor takes the
 * unselected hover colour.
 */
const tabStateRules = ({ rest, hovered, pressed, focused, selected, selectedHovered }: TabStates) => ({
  ...rest,
  [HOVER]: { "&:hover": hovered },
  "&:active": pressed,
  "&.Mui-focusVisible": focused,
  "&.Mui-selected": {
    ...selected,
    [HOVER]: { "&:hover": selectedHovered },
  },
});

const tabBase = (theme: Theme, size: TabsSize): CSSObject => ({
  ...theme.typography[TAB_LABEL[size]],
  // MUI sizes a tab 90 × 48 (72 with an icon); Figma's hug their content.
  minWidth: 0,
  minHeight: 0,
  gap: theme.spacing(TAB_ICON_GAP),
  "& .MuiTab-icon": {
    width: TAB_GLYPH[size],
    height: TAB_GLYPH[size],
    margin: 0,
    flexShrink: 0,
  },
  // The box fills to the glyph, and so does whatever icon or wrapper the call site put in it.
  [`& [${END_ICON_ATTRIBUTE}]`]: {
    display: "inline-flex",
    flexShrink: 0,
    width: TAB_GLYPH[size],
    height: TAB_GLYPH[size],
    "& > *, & svg": { width: "100%", height: "100%" },
  },
  // MUI adds `MuiTab-icon` only beside a label, so an icon-only tab's glyph is reached as a child.
  [`${ICON_ONLY} > *`]: { width: TAB_GLYPH[size], height: TAB_GLYPH[size] },
  // Figma has no disabled tab; MUI's own grey stands.
  "&.Mui-disabled": { color: theme.palette.text.disabled },
});

/**
 * Every tab is boxed on all four sides and its right edge is transparent, so the next tab's left stroke
 * is the only line between two — Figma's `order=first/middle/last` without per-position padding. The
 * transparent edge shows the tab's own fill, which is what Figma draws there. The stroke is inside the
 * box in Figma, so the padding gives its pixel back.
 */
const topBar = (theme: Theme, size: TabsSize): CSSObject => {
  const { px, py, iconOnly } = TOP_BAR_SIZES[size];
  const inset = (step: number) => `calc(${theme.spacing(step)} - ${TOP_BAR_BORDER}px)`;
  const radius = theme.shape.medium;

  return {
    "& .MuiTabs-indicator": { display: "none" },
    [TAB]: {
      ...tabBase(theme, size),
      padding: `${inset(py)} ${inset(px)}`,
      border: `${TOP_BAR_BORDER}px solid ${theme.palette.border.secondary}`,
      borderRightColor: "transparent",
      "&:first-of-type": { borderTopLeftRadius: radius, borderBottomLeftRadius: radius },
      // Before the states: on equal specificity the selected fill must also take the right edge.
      "&:last-of-type": {
        borderTopRightRadius: radius,
        borderBottomRightRadius: radius,
        borderRightColor: theme.palette.border.secondary,
      },
      [ICON_ONLY]: { padding: inset(iconOnly) },
      // Figma's `focusRipple`; MUI's pulse is kept and recoloured.
      "& .MuiTouchRipple-root": { color: theme.palette.secondary.light },
      ...tabStateRules(tabStates(theme, "top-bar")),
      "&.Mui-selected .MuiTouchRipple-root": { color: theme.palette.secondary.contrastText },
    },
  };
};

const bookmark = (theme: Theme): CSSObject => {
  const { px, py, iconOnly } = BOOKMARK_SIZE;

  return {
    "& .MuiTabs-indicator": { display: "none" },
    [TAB]: {
      ...tabBase(theme, "medium"),
      padding: theme.spacing(py, px),
      borderTopLeftRadius: theme.shape.medium,
      borderTopRightRadius: theme.shape.medium,
      [ICON_ONLY]: { padding: theme.spacing(iconOnly) },
      "& .MuiTouchRipple-root": { color: theme.palette.grey[400] },
      ...tabStateRules(tabStates(theme, "bookmark")),
    },
  };
};

/**
 * The underline is MUI's sliding indicator, recoloured. The strip's 1px rule is an inset shadow rather
 * than a border so the indicator, at `bottom: 0`, sits over it as Figma's does.
 */
const subNav = (theme: Theme, size: TabsSize, variant?: string): CSSObject => ({
  boxShadow: `inset 0 -1px 0 ${theme.palette.border.secondary}`,
  // A fixed strip clips nothing, but its `overflow: hidden` would cut the focus ring, which sits outside
  // the tab. A scrollable one has to clip, and keeps MUI's. `!important` because MUI writes the
  // scroller's `overflow: hidden` as an inline style (`Tabs.js`, `scrollerStyle`), which no rule outranks.
  ...(variant !== "scrollable" && {
    overflow: "visible",
    "& .MuiTabs-fixed": { overflow: "visible !important" },
  }),
  "& .MuiTabs-list": { gap: theme.spacing(SUB_NAV_GAP) },
  "& .MuiTabs-indicator": { backgroundColor: theme.palette.info.light },
  [HOVER]: {
    "&:has(.MuiTab-root.Mui-selected:hover) .MuiTabs-indicator": {
      backgroundColor: theme.palette.info.main,
    },
  },
  [TAB]: {
    ...tabBase(theme, size),
    padding: theme.spacing(0, 0, SUB_NAV_PADDING_BOTTOM[size]),
    ...tabStateRules(tabStates(theme, "sub-nav")),
  },
});

const APPEARANCE_STYLES: Record<
  TabsAppearance,
  (theme: Theme, size: TabsSize, variant?: string) => CSSObject
> = {
  "top-bar": topBar,
  bookmark: (theme) => bookmark(theme),
  "sub-nav": subNav,
};

const OWN_PROPS = new Set<PropertyKey>(["appearance", "size"]);

export const TabsRoot = styled(MuiTabs, {
  shouldForwardProp: (prop) => !OWN_PROPS.has(prop),
})<TabsRootProps>(({ theme, appearance, size, variant }) => ({
  minHeight: 0,
  ...APPEARANCE_STYLES[appearance](theme, size, variant),
}));
